import { Observable, of, from } from 'rxjs'
import { catchError, first, switchMap, map } from 'rxjs/operators'

import { ConditionReport } from '@skinsoft/types'
import { IndexedDBCache } from "@skinsoft/offline"
import { fetch, DEFAULT_PROXY } from '@skinsoft/network'
import { ConditionReportDataStore } from "@skinsoft/entity"
import { MediaTransferHelper } from './media-transfer-helper'
import { DATE_FORMATS, formatCurrentDate } from '@skinsoft/utils'

export class ConditionReportPDFTransferHelper {

    public static getInstance() {
        if (!ConditionReportPDFTransferHelper.instance) {
            ConditionReportPDFTransferHelper.instance = new ConditionReportPDFTransferHelper();
        }

        return ConditionReportPDFTransferHelper.instance;
    }

    public static install(Vue) {
        Vue.prototype.$conditionReportPDFTransferHelper = ConditionReportPDFTransferHelper.getInstance();
    }

    private static instance: ConditionReportPDFTransferHelper = null as any;
    private cache = new IndexedDBCache();
    private proxy = DEFAULT_PROXY;

    public downloadOriginalPDFFile(mediaId: string): Observable<any> {
        return this.proxy.fetch("/downloadMedias/originalFile",
            {
                "mediaId": mediaId,
            },
            'GET', undefined, undefined, true, true)
            .pipe(
                catchError((error) => of(undefined)),
                first())
    }

    /**
     * 
     * @returns the id of the pdf on the API
     */
    public uploadPDFDocument(mimeType: string, noticeId: string, file: File, isAssociatedDocument?): Observable<string> {
        // Setting image file in request body
        let fileName = file.name
        if(!fileName) {
            fileName = formatCurrentDate(DATE_FORMATS.DD_MM_YYYY_DASH_HMS_ONE_BLOCK) + '.pdf'
        }
        const formData = new FormData();
        formData.append("files", file, fileName)

        // Uploading image to backend
        return fetch.api(`/uploadMedias/filesGroup?mimeTypes=${mimeType}`, { method: 'PUT', body: formData }).pipe(
            switchMap(  
                groupId => {
                    return fetch.api(`/uploadMedias/beginIngest?groupId=${groupId}&noticeId=${noticeId}&noticeType=conditionReport&isPrincipal=true&isAssociatedDocument=${!!isAssociatedDocument}`,
                        {
                            method: 'PUT',
                        })
                }
            ),
            switchMap(() => {
                return from(MediaTransferHelper.getInstance().repeatFindingMediaIdFromFileName(fileName))
            }),
            map(media => media.id)
        )
    }

    /**
     * 
     * @returns the pdf's cache key
     */
    public uploadPDFDocumentInCache(file: File, conditionReport: ConditionReport, conditionReportDataStore: ConditionReportDataStore): Observable<string> {
        return from(new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            
            reader.onerror = () => {
                reject()
            }

            reader.addEventListener('load', async () => {
                let base64Pdf = reader.result
                if(base64Pdf == null) {
                    base64Pdf = '';
                }else if(base64Pdf instanceof ArrayBuffer){
                    base64Pdf = base64Pdf.toString();
                }
                
                const cacheKeyPDF = `local:${file.name}`
                await this.cache.write(cacheKeyPDF, base64Pdf as any)


                let updatedMedias = {...conditionReport.associatedMedias ?? { associatedMedias: [] }}
                updatedMedias = MediaTransferHelper.permutateFirstMedia(cacheKeyPDF, conditionReport.associatedMedias)
                updatedMedias.medias?.push(cacheKeyPDF)

                conditionReportDataStore.updateOfflineMode(conditionReport.id, {
                    associatedMedias: updatedMedias
                }).subscribe(() => {
                    resolve(cacheKeyPDF)
                })

            },false)

            reader.readAsDataURL(file)
        }))
    }

    public deletePDFInCache(key: string) {
        this.cache.remove(key)
    }
}